import { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '@/integrations/supabase/supabase';
import { CLIENT_BROWSER_FIELDS } from '@/domains/auth/api/clientFields';
import { fetchProfileFromAuthUserId } from '@/domains/users';
import { useAuthContext } from './AuthContext';

const ClientContext = createContext();

export const ClientProvider = ({ children }) => {
  const { user, isLoading: isAuthLoading } = useAuthContext();
  const [clientId, setClientId] = useState(null);
  const [client, setClient] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch profile and clientId when user changes (after auth is ready)
  useEffect(() => {
    if (isAuthLoading) return;

    if (!user) {
      setClientId(null);
      setClient(null);
      setProfile(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let current = true;
    const fetchClientData = async () => {
      setIsLoading(true);
      setError(null);
      setClientId(null);
      setClient(null);
      setProfile(null);
      try {
        const fetchedProfile = await fetchProfileFromAuthUserId(user.id);
        if (!current) return;
        const fetchedClientId = fetchedProfile?.client_id;
        if (!fetchedClientId) {
          throw new Error('Your account has no client assigned. Please contact support.');
        }
        const { data, error: clientError } = await supabase
          .from('clients')
          .select(CLIENT_BROWSER_FIELDS)
          .eq('client_id', fetchedClientId)
          .single();
        if (!current) return;
        if (clientError) throw clientError;
        if (!data) throw new Error('Your client could not be loaded. Please sign in again.');
        setProfile(fetchedProfile);
        setClientId(fetchedClientId);
        setClient(data);
      } catch (err) {
        if (current) setError(err);
      } finally {
        if (current) setIsLoading(false);
      }
    };

    fetchClientData();
    return () => { current = false; };
  }, [user, isAuthLoading]);

  return (
    <ClientContext.Provider value={{
      clientId,
      client,
      profile,
      isLoading,
      error,
    }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClientContext must be used within a ClientProvider');
  }
  return context;
};
